import { ChevronDown } from "lucide-react";
import type { CSSProperties } from "react";
import {
    getUnitCategoryConfiguration,
    isKnownUnitCategory,
} from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { getUnitCategoryName } from "verdi-common/i18n";
import { Button } from "@/components/ui/button";
import {
    Menu,
    MenuGroup,
    MenuGroupLabel,
    MenuPopup,
    MenuRadioGroup,
    MenuRadioItem,
    MenuSeparator,
    MenuTrigger,
} from "@/components/ui/menu";
import { useSupportedLocale } from "@/hooks/use-locale";
import { cn } from "@/utils/class-name";
import { AxisSide, type AxisSpec } from "../../ChartSpec";
import { useDataViewerStore } from "../../data-viewer-provider";
import {
    UNIT_BADGE_GRID_GAP_PX,
    UNIT_BADGE_METRIC_PICKER_ICON_CLASS,
    UNIT_BADGE_PADDING_CLASS,
    UNIT_BADGE_TYPE_CLASS,
} from "../unit-badge-tokens";

/**
 * Sit a fixed gap outside the chrome layer's own edge (the layer's box IS the plot rect, so this
 * negative offset from the layer reproduces the same visual position the old gridMargin-based
 * math did, without needing to know the absolute gutter width).
 */
function unitBadgePositionStyle(side: AxisSide): CSSProperties {
    if (side === AxisSide.Left) {
        return { left: -UNIT_BADGE_GRID_GAP_PX, transform: "translateX(-100%)" };
    }
    return { right: -UNIT_BADGE_GRID_GAP_PX, transform: "translateX(100%)" };
}

export function UnitBadge({
    label,
    side,
    unitCategory,
    currentUnit,
    clickable,
    axisCandidates = [],
    activeAxisId,
    onActiveAxisChange,
}: {
    label: string;
    side: AxisSide;
    unitCategory: string;
    currentUnit: Unit | null;
    clickable: boolean;
    axisCandidates?: AxisSpec[];
    activeAxisId?: string;
    onActiveAxisChange?: (axisId: string) => void;
}) {
    const setUnitOverride = useDataViewerStore((state) => state.setUnitOverride);
    const locale = useSupportedLocale();
    const positionStyle = {
        top: 0,
        ...unitBadgePositionStyle(side),
    };

    const allowedUnits = getUnitCategoryConfiguration(unitCategory)?.allowedUnits ?? [];
    const hasMetricPicker = axisCandidates.length > 1 && activeAxisId != null && onActiveAxisChange != null;
    const hasUnitPicker = clickable && allowedUnits.length > 1;
    const isButtonDisabled = !hasMetricPicker && !hasUnitPicker;

    let tooltip: string | undefined;
    if (hasMetricPicker) {
        tooltip = "Choose y-axis";
    } else if (hasUnitPicker) {
        tooltip = "Change unit";
    }

    const badgeClassName = cn(
        "pointer-events-auto absolute z-1 font-normal leading-none",
        UNIT_BADGE_PADDING_CLASS,
        UNIT_BADGE_TYPE_CLASS,
    );

    // No pickers: same outline look as an interactive badge, but not a menu trigger (and not faded).
    // Disabled Button (full opacity) keeps keyboard/mouse from opening an empty Menu; prefer this over
    // pointer-events-none alone, which still left the control focusable.
    if (isButtonDisabled) {
        return (
            <Button
                size={"xs"}
                variant={"outline"}
                disabled
                className={cn(badgeClassName, "cursor-default disabled:opacity-100")}
                style={positionStyle}
            >
                {label}
            </Button>
        );
    }

    return (
        <Menu>
            <MenuTrigger
                render={
                    <Button
                        size={"xs"}
                        variant={"outline"}
                        className={badgeClassName}
                        style={positionStyle}
                        tooltip={tooltip}
                    />
                }
            >
                {label}
                {hasMetricPicker && <ChevronDown className={cn(UNIT_BADGE_METRIC_PICKER_ICON_CLASS, "opacity-80")} />}
            </MenuTrigger>
            <MenuPopup side={"bottom"} align={"start"} sideOffset={2}>
                {hasMetricPicker ? (
                    <MenuGroup>
                        <MenuGroupLabel>Metric</MenuGroupLabel>
                        <MenuRadioGroup
                            value={activeAxisId}
                            onValueChange={(axisId) => {
                                if (axisId) onActiveAxisChange(axisId);
                            }}
                        >
                            {axisCandidates.map((axis) => (
                                <MenuRadioItem key={axis.id} value={axis.id} closeOnClick={false}>
                                    {getUnitCategoryName(axis.unitCategory, locale)}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
                {hasMetricPicker && hasUnitPicker ? <MenuSeparator /> : null}
                {hasUnitPicker ? (
                    <MenuGroup>
                        {hasMetricPicker ? <MenuGroupLabel>Unit</MenuGroupLabel> : null}
                        <MenuRadioGroup
                            value={currentUnit}
                            onValueChange={(unit) => {
                                if (unit !== null && isKnownUnitCategory(unitCategory)) {
                                    setUnitOverride(unitCategory, unit as Unit);
                                }
                            }}
                        >
                            {allowedUnits.map((unit) => (
                                <MenuRadioItem key={unit} value={unit} closeOnClick>
                                    {UnitConfigurations[unit]?.symbol ?? unit}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
            </MenuPopup>
        </Menu>
    );
}
